/**
 * activeSessionsStore.ts — Global Zustand store for WebSocket session pool state.
 *
 * PASSIVE MIRROR: this store contains only setters with no business logic and
 * no side effects. All delta reduction is the responsibility of
 * StreamingSessionReducer. The store exists solely to make session state
 * available across page navigations without prop-drilling.
 *
 * Two slices are intentionally separate for render performance:
 * - `sessions`     — updated at ~50 Hz during streaming
 * - `connections`  — updated only on reconnect events (rare)
 *
 * Components that only care about connection health (e.g. a reconnect banner)
 * select from `connections` and are immune to the high-frequency `sessions`
 * updates.
 */

import { create } from 'zustand';
import { IDLE_CONTEXT_COMPACTION } from '../types/session.types';
import type { SessionData, ConnectionState } from '../types/session.types';
import type { TimelineItem } from '../types/session.types';

// ============================================================================
// Store interface
// ============================================================================

/** Phantom conversation awaiting backend confirmation. Lives in Zustand only. */
interface PendingConversation {
  id: string;
  caseId: string | null;
  createdAt: number;
}

interface ActiveSessionsStore {
  // ----- State ---------------------------------------------------------------

  /** All live session slots, keyed by sessionKey. Updated at streaming frequency. */
  sessions: Record<string, SessionData>;

  /** WebSocket connection health per session. Updated only on reconnect events. */
  connections: Record<string, ConnectionState>;

  /**
   * Phantom conversations shown in the sidebar before the backend creates the DB row.
   * Keyed by conversation UUID. Auto-removed only after the global HTTP list
   * observes the DB row; a WebSocket confirmation does not populate that cache.
   */
  pendingConversations: Record<string, PendingConversation>;

  /**
   * Conversations dont le SERVEUR a confirmé la ligne en base, keyed by UUID.
   *
   * C'est la trace observable de la traduction `conversation_created` →
   * `conversation_confirmed` : le backend n'émet cet événement qu'après
   * l'INSERT. Rien d'autre ne prouve la persistance d'un fil — ni l'UUID frappé
   * par le client, ni l'URL qui le porte, ni le fantôme de la barre latérale,
   * ni la paternité, ni un socket en `streaming`.
   *
   * Cette carte existe parce que la confirmation n'atterrissait NULLE PART :
   * `confirmConversationId` réécrivait un `conversationId` déjà posé à la
   * création de la session (`createSession`, #238), donc lire ce champ rendait
   * la même valeur avant et après la preuve. Un lecteur qui s'y fiait ne
   * distinguait pas un fil persisté d'un fil purement local.
   *
   * ⚠️ Ce n'est PAS un accusé de sauvegarde du message : la confirmation
   * précède la persistance du message lui-même, et n'attend pas la réponse de
   * l'agent. Elle prouve l'existence du FIL, rien de plus.
   *
   * Session-only : vidé par `clear()` à la déconnexion, jamais persisté. Après
   * un rechargement, la preuve d'existence redevient la donnée serveur
   * (détail REST / liste globale), qui fait tout aussi bien autorité.
   */
  confirmedConversations: Record<string, true>;

  /**
   * Conversations dont CE client a émis le premier message, keyed by UUID.
   *
   * Distinct de `pendingConversations`, qui est un artefact d'affichage retiré
   * dès que la row DB existe. La paternité, elle, ne s'annule pas : avoir créé
   * la conversation prouve qu'on en est owner, sans attendre aucun aller-retour
   * REST. Sans cette distinction, `conversation_confirmed` retirait le phantom
   * et laissait le composer sans autorité — donc bloqué en « Chargement de
   * l'accès » — le temps que le détail réponde (incident 2026-08-09, réseau
   * faible : blocage pendant que l'agent répondait déjà).
   *
   * Session-only : vidé par `clear()` à la déconnexion, jamais persisté.
   */
  authoredConversations: Record<string, true>;

  // ----- Session setters -----------------------------------------------------

  /**
   * Register a new session slot with initial state.
   *
   * Call once when the pool allocates a new slot.
   * Idempotent: calling again with the same sessionKey is a no-op if the
   * session already exists, so repeated mounts don't wipe live state.
   */
  createSession(sessionKey: string, conversationId: string, caseId: string | null): void;

  /**
   * Shallow-merge arbitrary fields into an existing session.
   *
   * Used for status transitions and scalar field updates (e.g. `memoryUsage`,
   * `lastActivityAt`). Does NOT touch `timeline` — use `setTimeline` for that.
   */
  setSession(sessionKey: string, patch: Partial<SessionData>): void;

  /**
   * Replace the live timeline for a session.
   *
   * Called by StreamingSessionReducer on every incoming WS event that mutates
   * the timeline. Replaces the entire array (not patch) to avoid stale item
   * references leaking across exchanges.
   */
  setTimeline(sessionKey: string, timeline: TimelineItem[]): void;

  /**
   * Enregistre la confirmation serveur d'un fil (`conversation_created`).
   *
   * Le `conversationId` est déjà connu de la session — le frontend le frappe
   * lui-même (INV-3). Ce que cet appel apporte est donc la PREUVE, pas
   * l'identifiant : il inscrit le fil dans `confirmedConversations`, seul
   * endroit où l'existence en base devient lisible.
   */
  confirmConversationId(sessionKey: string, conversationId: string): void;

  /**
   * Remove a session from the store entirely.
   *
   * Call when the slot is evicted or the session expires.
   * Safe to call if the session does not exist (no-op).
   */
  removeSession(sessionKey: string): void;

  // ----- Connection setters --------------------------------------------------

  /**
   * Shallow-merge connection health fields for a session.
   *
   * Called on WS open, close, reconnect progress, and reconnect failure.
   * Creates the entry if it does not exist.
   */
  setConnection(sessionKey: string, patch: Partial<ConnectionState>): void;

  /**
   * Remove the connection entry for a session.
   *
   * Call when the session is fully torn down (after `removeSession`).
   */
  removeConnection(sessionKey: string): void;

  // ----- Pending conversation setters ----------------------------------------

  /**
   * Register a phantom conversation for optimistic sidebar display.
   *
   * Called when the first message mints its UUID — the phantom appears immediately
   * in the sidebar before any WS connection or DB row exists.
   */
  addPendingConversation(id: string, caseId: string | null): void;

  /**
   * Enregistre définitivement (pour la session) qu'on est l'auteur de `id`.
   *
   * Appelé au même moment que `addPendingConversation`, mais jamais annulé :
   * c'est la seule autorité d'accès disponible entre le premier envoi et la
   * résolution du détail REST.
   */
  markConversationAuthored(id: string): void;

  /**
   * Remove a phantom conversation.
   *
   * Called when: (a) the global API list observes the persisted row, (b) the
   * first send rolls back before persistence, or (c) the user deletes a phantom.
   */
  removePendingConversation(id: string): void;

  // ----- Cleanup -------------------------------------------------------------

  /** Reset the entire store to initial state. Used in tests and on sign-out. */
  clear(): void;
}

// ============================================================================
// Initial values
// ============================================================================

const initialState = {
  sessions: {} as Record<string, SessionData>,
  connections: {} as Record<string, ConnectionState>,
  pendingConversations: {} as Record<string, PendingConversation>,
  confirmedConversations: {} as Record<string, true>,
  authoredConversations: {} as Record<string, true>,
};

// ============================================================================
// Store implementation
// ============================================================================

export const useActiveSessionsStore = create<ActiveSessionsStore>()((set, get) => ({
  ...initialState,

  // ---- Session setters -------------------------------------------------------

  createSession: (sessionKey, conversationId, caseId) => {
    // Guard: do not overwrite an already-live session on repeated mounts.
    if (get().sessions[sessionKey] !== undefined) {
      return;
    }

    const session: SessionData = {
      sessionKey,
      // conversationId is known up-front (the frontend generates the UUID — INV-3),
      // so set it NOW rather than leaving it null until backend confirmation. The
      // URL→sessionKey reattach (AgentV3View) matches on SessionData.conversationId;
      // a null here would make it detach the freshly-created session right after we
      // navigate to ?c=<id>, blanking the view. confirmConversationId stays as a
      // harmless idempotent re-set. (#238)
      conversationId,
      caseId,
      status: 'connecting',
      timeline: [],
      memoryUsage: null,
      // Inconnue jusqu'au premier `connection_established` générique (pont `compact_context_capability_changed`).
      compactContextCapability: 'unknown',
      compaction: IDLE_CONTEXT_COMPACTION,
      lastActivityAt: Date.now(),
      turnStartedAt: null, // set on first 'streaming' transition (cf. status bridge)
    };

    set((state) => ({
      sessions: { ...state.sessions, [sessionKey]: session },
    }));
  },

  setSession: (sessionKey, patch) => {
    set((state) => {
      const existing = state.sessions[sessionKey];
      if (existing === undefined) {
        return state;
      }
      return {
        sessions: {
          ...state.sessions,
          [sessionKey]: { ...existing, ...patch },
        },
      };
    });
  },

  setTimeline: (sessionKey, timeline) => {
    set((state) => {
      const existing = state.sessions[sessionKey];
      if (existing === undefined) {
        return state;
      }
      return {
        sessions: {
          ...state.sessions,
          [sessionKey]: { ...existing, timeline },
        },
      };
    });
  },

  confirmConversationId: (sessionKey, conversationId) => {
    set((state) => {
      // La preuve d'existence s'enregistre même si le slot a déjà été évincé :
      // c'est le FIL qui est confirmé, pas la session de transport qui l'a
      // appris. Un slot recyclé ne doit pas effacer ce que le serveur a dit.
      const confirmedConversations = state.confirmedConversations[conversationId] === true
        ? state.confirmedConversations
        : { ...state.confirmedConversations, [conversationId]: true as const };
      const existing = state.sessions[sessionKey];
      if (existing === undefined) {
        return { confirmedConversations };
      }
      return {
        confirmedConversations,
        sessions: {
          ...state.sessions,
          [sessionKey]: { ...existing, conversationId },
        },
      };
    });
  },

  removeSession: (sessionKey) => {
    set((state) => {
      if (state.sessions[sessionKey] === undefined) {
        return state;
      }
      const next = { ...state.sessions };
      delete next[sessionKey];
      return { sessions: next };
    });
  },

  // ---- Connection setters ----------------------------------------------------

  setConnection: (sessionKey, patch) => {
    set((state) => {
      const existing = state.connections[sessionKey];
      const base: ConnectionState = existing ?? {
        isConnected: false,
        reconnectInfo: null,
        reconnectFailed: false,
      };
      return {
        connections: {
          ...state.connections,
          [sessionKey]: { ...base, ...patch },
        },
      };
    });
  },

  removeConnection: (sessionKey) => {
    set((state) => {
      if (state.connections[sessionKey] === undefined) {
        return state;
      }
      const next = { ...state.connections };
      delete next[sessionKey];
      return { connections: next };
    });
  },

  // ---- Pending conversation setters ------------------------------------------

  addPendingConversation: (id, caseId) => {
    set((state) => ({
      pendingConversations: {
        ...state.pendingConversations,
        [id]: { id, caseId, createdAt: Date.now() },
      },
    }));
  },

  markConversationAuthored: (id) => {
    set((state) => (
      state.authoredConversations[id] === true
        ? state
        : { authoredConversations: { ...state.authoredConversations, [id]: true } }
    ));
  },

  removePendingConversation: (id) => {
    set((state) => {
      if (state.pendingConversations[id] === undefined) {
        return state;
      }
      const { [id]: _, ...rest } = state.pendingConversations;
      return { pendingConversations: rest };
    });
  },

  // ---- Cleanup ---------------------------------------------------------------

  clear: () => {
    set({
      sessions: {},
      connections: {},
      pendingConversations: {},
      confirmedConversations: {},
      authoredConversations: {},
    });
  },
}));
